import { bigint, boolean, integer, pgTable, text, timestamp, unique, index, foreignKey } from 'drizzle-orm/pg-core';
import { user } from './user';
import { sql } from 'drizzle-orm';

export const tag = pgTable('Tag', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  name: text().notNull(),
  color: text().notNull().default('#6366f1'),
  description: text(),
  languages: text().array().notNull().default(sql`ARRAY[]::text[]`),
  botIds: integer().array().notNull().default(sql`ARRAY[]::integer[]`),
  // Каналы привязки аккаунта, которых тег требует от пользователя: EMAIL | TELEGRAM | GOOGLE.
  // Пустой массив — тег ничего не требует. Логика «нужны ВСЕ перечисленные».
  requiredChannels: text().array().notNull().default(sql`ARRAY[]::text[]`),
  // Если true — для EMAIL-канала этого тега OTP-подтверждение не обязательно:
  // при наличии EMAIL-приглашения на введённый адрес почта верифицируется сразу.
  skipEmailOtp: boolean().notNull().default(false),
  // Если true — пользователь может скрыть модалку «Привязка аккаунта» навсегда
  // (чекбокс «Больше не показывать»). Имеет смысл только при непустом requiredChannels.
  // Модалка скрывается, только если ВСЕ требующие теги юзера разрешают скрытие.
  linkModalDismissible: boolean().notNull().default(false),
  deletedAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull().$onUpdate(() => new Date()),
});

// Журнал случаев, когда пользователь с тегом-требованием ввёл и подтвердил почту,
// но EMAIL-приглашения на этот адрес не нашлось. Отображается на странице тега в админке.
export const tagEmailWithoutInvitation = pgTable('TagEmailWithoutInvitation', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  tagId: integer().notNull(),
  userId: integer().notNull(),
  email: text().notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  unique('TagEmailWithoutInvitation_tag_user_unique').on(table.tagId, table.userId),
  index('TagEmailWithoutInvitation_tagId_idx').on(table.tagId),
  foreignKey({ columns: [table.tagId], foreignColumns: [tag.id] }).onDelete('cascade'),
  foreignKey({ columns: [table.userId], foreignColumns: [user.id] }).onDelete('cascade'),
]);

export const utmSource = pgTable('UtmSource', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  name: text().notNull(),
  displayName: text().notNull(),
  shortCode: text().notNull(),
  autoTagId: integer(),
  // Целевая страница «переход после открытия»: внутренний путь платформы
  // (/courses/123, /dashboard) или внешний https-URL. NULL — без перехода.
  // После регистрации/авторизации пользователя платформа уводит на эту цель.
  redirectUrl: text(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull().$onUpdate(() => new Date()),
}, (table) => [
  unique('UtmSource_name_unique').on(table.name),
  unique('UtmSource_shortCode_unique').on(table.shortCode),
  index('UtmSource_name_idx').on(table.name),
  foreignKey({ columns: [table.autoTagId], foreignColumns: [tag.id] }).onDelete('set null'),
]);

/**
 * UtmVisit — каждый клик по UTM-ссылке (бот /start, Mini App startapp, web ?utm_source=).
 *
 * Цели: считать переходы по каналам (включая анонимных гостей), показывать в админке
 * разбивку «Визиты» с фильтром по каналу/боту, IP/UA для web-гостей.
 *
 * Поля:
 *  - utmSourceId: какой UTM-источник (FK на UtmSource).
 *  - channel: 'bot' | 'miniapp' | 'web'.
 *  - botId: telegramBot.id для bot/miniapp каналов; NULL для web.
 *  - userId: если визит от залогиненного — FK на User; NULL для гостя.
 *  - telegramId: для bot/miniapp визитов до создания юзера — telegramId из update/initData.
 *  - ip / userAgent: для web/miniapp гостей; NULL для bot (Telegram не отдаёт IP).
 *
 * FK на telegramBot НЕ ставим: telegramBot живёт в schema/telegram.ts, чтобы избежать
 * циклов импорта оставляем колонку без явного FK (целостность не критична — UI tolerates).
 */
export const utmVisit = pgTable('UtmVisit', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  utmSourceId: integer().notNull(),
  channel: text().notNull(),
  botId: integer(),
  userId: integer(),
  telegramId: bigint({ mode: 'number' }),
  ip: text(),
  userAgent: text(),
  // ISO-2 страны, резолвится через geoIpService.getCountry(ip) на сервере.
  // Для bot-визитов (нет IP) — NULL.
  country: text(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('UtmVisit_utmSourceId_idx').on(table.utmSourceId),
  index('UtmVisit_channel_idx').on(table.channel),
  index('UtmVisit_createdAt_idx').on(table.createdAt),
  index('UtmVisit_userId_idx').on(table.userId),
  index('UtmVisit_country_idx').on(table.country),
  foreignKey({ columns: [table.utmSourceId], foreignColumns: [utmSource.id] }).onDelete('cascade'),
  foreignKey({ columns: [table.userId], foreignColumns: [user.id] }).onDelete('set null'),
]);

/**
 * UtmRedirect — отложенный переход по UTM-ссылке с целевой страницей.
 *
 * Строка ПОЯВЛЯЕТСЯ, когда пользователь открыл UTM-ссылку источника с
 * redirectUrl (web/miniapp визит или /start в боте), и УДАЛЯЕТСЯ, когда
 * платформа фактически увела его на цель (POST /public/utm/redirect/complete).
 * Живые строки = «открыл, но не перешёл» — показываются в админке.
 *
 * Идентификация до регистрации:
 *  - telegramId — для bot/miniapp (из update / initData);
 *  - visitorId — для web-гостей (анонимный id в cookie mc_visitor, генерирует платформа);
 *  - userId — проставляется/используется, если юзер уже известен.
 *
 * targetUrl — снапшот цели на момент открытия: смена redirectUrl у источника
 * не меняет уже выданные переходы.
 */
export const utmRedirect = pgTable('UtmRedirect', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  utmSourceId: integer().notNull(),
  channel: text().notNull(),
  botId: integer(),
  userId: integer(),
  telegramId: bigint({ mode: 'number' }),
  visitorId: text(),
  targetUrl: text().notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('UtmRedirect_utmSourceId_idx').on(table.utmSourceId),
  index('UtmRedirect_telegramId_idx').on(table.telegramId),
  index('UtmRedirect_visitorId_idx').on(table.visitorId),
  index('UtmRedirect_userId_idx').on(table.userId),
  foreignKey({ columns: [table.utmSourceId], foreignColumns: [utmSource.id] }).onDelete('cascade'),
  foreignKey({ columns: [table.userId], foreignColumns: [user.id] }).onDelete('cascade'),
]);
